import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SPECTRUM_GUIDE_CONFIG } from './guide.config';
import { HelpTourStep, HelpWorkflow } from './guide.model';
import { GuideProgressService } from './guide-progress.service';
import { GuideRegistry, HelpTopicRef } from './guide-registry';

export type GuideTab = 'screen' | 'manual';

export interface GuideTour {
    topicId: string;
    steps: HelpTourStep[];
    index: number;
}

/** The first-visit offer of a tour. */
export interface GuideNudge {
    ref: HelpTopicRef;
    /** The tour of Spectrum itself, offered once on the first screen a user ever opens. */
    welcome: boolean;
}

/** The basics topic that carries the tour of the frame every module shares. */
export const SPECTRUM_WELCOME_TOPIC = 'spectrum.getting-around';

/**
 * The Guide's state for the whole app: whether the panel is open and on which
 * tab, the screen it is about, the tour running, the walkthrough pinned. The
 * `<spectrum-guide>` host tells it where the user is; everything else reads it.
 */
@Injectable({ providedIn: 'root' })
export class GuideService {
    private router = inject(Router);
    private zone = inject(NgZone);
    private config = inject(SPECTRUM_GUIDE_CONFIG, { optional: true });
    readonly registry = inject(GuideRegistry);
    readonly progress = inject(GuideProgressService);

    open = false;
    tab: GuideTab = 'screen';

    /** The help topic for the screen on show, if one is written. */
    current: HelpTopicRef | null = null;
    /** The page's own name, for screens with no topic yet. */
    screenLabel = '';

    tour: GuideTour | null = null;
    nudge: GuideNudge | null = null;
    private nudgeTimer: ReturnType<typeof setTimeout> | null = null;
    private url = '';
    private loading: Promise<void> | null = null;

    /** False until the module's manual has arrived; the panel says it is loading. */
    manualReady = false;
    manualFailed = false;

    /** Whether this app has turned the Guide on (`provideSpectrumGuide`). */
    get enabled(): boolean {
        return !!this.config;
    }

    get moduleTitle(): string {
        return this.registry.home?.title || this.config?.title || 'Spectrum';
    }

    get moduleId(): string {
        return this.registry.home?.module || this.config?.module || this.registry.basics.module;
    }

    /** Called by the Guide's host on every navigation. */
    setScreen(url: string, screenLabel = ''): void {
        this.url = url;
        this.screenLabel = screenLabel;
        this.current = this.registry.topicForUrl(url);
        this.nudge = null;
        // A tour can only be offered once the screen's topic is known, and
        // the manual is small next to what a screen loads, so it is fetched
        // on the first navigation rather than waiting for the panel.
        this.ensureManual().then(() => this.scheduleNudge());
    }

    /** Fetches the module's manual once, and puts the screen's topic in place when it arrives. */
    ensureManual(): Promise<void> {
        if (!this.config) {
            this.manualReady = true;
            return Promise.resolve();
        }
        if (!this.loading) {
            this.loading = this.config.manual().then(
                manual => this.zone.run(() => {
                    this.registry.setHome(manual);
                    this.manualReady = true;
                    this.manualFailed = false;
                    this.current = this.registry.topicForUrl(this.url);
                }),
                () => this.zone.run(() => {
                    // A failed chunk (a deploy mid-session) may load on the next try.
                    this.manualFailed = true;
                    this.loading = null;
                }),
            );
        }
        return this.loading;
    }

    toggle(): void {
        this.open ? this.close() : this.show();
    }

    show(tab?: GuideTab): void {
        this.ensureManual();
        this.open = true;
        if (tab) {
            this.tab = tab;
        }
        this.nudge = null;
    }

    close(): void {
        this.open = false;
    }

    // ---- tours ---------------------------------------------------------

    /** The tour for this screen, or the tour of Spectrum where the screen has none. */
    get screenTour(): HelpTopicRef | null {
        if (this.current?.topic.tour?.length) {
            return this.current;
        }
        return this.registry.topicById(SPECTRUM_WELCOME_TOPIC);
    }

    startTour(ref: HelpTopicRef | null = this.screenTour): void {
        if (!ref?.topic.tour?.length) {
            return;
        }
        this.nudge = null;
        this.tour = { topicId: ref.topic.id, steps: ref.topic.tour, index: 0 };
        // Offered once: starting it is enough, whether or not it is finished.
        this.progress.markTour(ref.topic.id, 'taken');
    }

    startWelcomeTour(): void {
        this.startTour(this.registry.topicById(SPECTRUM_WELCOME_TOPIC));
    }

    endTour(): void {
        this.tour = null;
        // After the tour of Spectrum, the screen's own tour is next in line.
        this.scheduleNudge();
    }

    dismissNudge(): void {
        if (this.nudge) {
            this.progress.markTour(this.nudge.ref.topic.id, 'dismissed');
        }
        this.nudge = null;
        this.scheduleNudge();
    }

    /**
     * A tour is offered on the first visit to a screen that has one. A user's
     * very first screen offers the tour of Spectrum instead, since the frame
     * comes before any one screen.
     */
    private scheduleNudge(): void {
        if (this.nudgeTimer) {
            clearTimeout(this.nudgeTimer);
        }
        const expected = this.url;
        this.nudgeTimer = setTimeout(() => this.zone.run(() => {
            if (this.url !== expected || this.tour || this.open || !this.enabled) {
                return;
            }
            const welcome = this.registry.topicById(SPECTRUM_WELCOME_TOPIC);
            if (welcome && !this.progress.tourState(SPECTRUM_WELCOME_TOPIC)) {
                this.nudge = { ref: welcome, welcome: true };
            } else if (this.current?.topic.tour?.length && !this.progress.tourState(this.current.topic.id)) {
                this.nudge = { ref: this.current, welcome: false };
            }
        }), 1400);
    }

    /** Whether this screen's tour is still untaken — the dot on the Guide button. */
    get tourPending(): boolean {
        const ref = this.current;
        return !!ref?.topic.tour?.length && !this.progress.tourState(ref.topic.id);
    }

    // ---- walkthroughs --------------------------------------------------

    get workflow(): { workflow: HelpWorkflow; step: number } | null {
        const saved = this.progress.workflow;
        const workflow = saved ? this.registry.workflowById(saved.id) : null;
        return workflow && saved ? { workflow, step: Math.min(saved.step, workflow.steps.length - 1) } : null;
    }

    startWorkflow(id: string): void {
        this.progress.setWorkflow({ id, step: 0 });
        this.show('screen');
        this.goToWorkflowStep();
    }

    nextWorkflowStep(): void {
        const active = this.workflow;
        if (!active) {
            return;
        }
        if (active.step >= active.workflow.steps.length - 1) {
            this.progress.setWorkflow(null);
            return;
        }
        this.progress.setWorkflow({ id: active.workflow.id, step: active.step + 1 });
        this.goToWorkflowStep();
    }

    previousWorkflowStep(): void {
        const active = this.workflow;
        if (active && active.step > 0) {
            this.progress.setWorkflow({ id: active.workflow.id, step: active.step - 1 });
            this.goToWorkflowStep();
        }
    }

    stopWorkflow(): void {
        this.progress.setWorkflow(null);
    }

    /** Opens the screen the walkthrough's current step is on, when it has one to open. */
    goToWorkflowStep(): void {
        const active = this.workflow;
        const topicId = active?.workflow.steps[active.step].topic;
        const route = topicId ? routeOf(this.registry.topicById(topicId)) : null;
        if (route) {
            this.router.navigateByUrl(route);
        }
    }

    /** Whether the walkthrough's current step is about the screen on show. */
    get workflowStepHere(): boolean {
        const active = this.workflow;
        return !!active && active.workflow.steps[active.step].topic === this.current?.topic.id;
    }

    get workflowStepHasScreen(): boolean {
        const active = this.workflow;
        const topicId = active?.workflow.steps[active.step].topic;
        return !!topicId && !!routeOf(this.registry.topicById(topicId));
    }

    // ---- links ---------------------------------------------------------

    /** Opens a topic's screen when it has a plain route; otherwise its page in the Help centre. */
    openTopic(ref: HelpTopicRef): void {
        const route = routeOf(ref);
        this.tab = 'screen';
        this.router.navigateByUrl(route || `/help/${ref.manual.module}?topic=${encodeURIComponent(ref.topic.id)}`);
    }

    topicRoute(ref: HelpTopicRef): string | null {
        return routeOf(ref);
    }
}

/**
 * A topic's route without parameters, if it has one — a screen reached only by
 * id cannot be opened blind, and a basics topic (which belongs to no app's
 * routes) has none.
 */
function routeOf(ref: HelpTopicRef | null): string | null {
    if (!ref) {
        return null;
    }
    return [ref.topic.route, ...(ref.topic.aliases || [])].find(route => !!route && !route.includes(':')) || null;
}
